import React, { useState } from 'react';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './AuthPanel.css';
import axios from '../axiosConfig';
import AlertMessage from './AlertMessage';

const AuthPanel = ({ onLoginSuccess }) => {
  const [loginCredentials, setLoginCredentials] = useState({ email: '', password: '' });
  const [signupCredentials, setSignupCredentials] = useState({ email: '', password: '', confirmPassword: '' });
  const [resetEmail, setResetEmail] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [alert, setAlert] = useState(null);
  const [user] = useAuthState(auth);

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginCredentials({ ...loginCredentials, [name]: value });
  };

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupCredentials({ ...signupCredentials, [name]: value });
  };

  const handleResetEmailChange = (e) => {
    setResetEmail(e.target.value);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, loginCredentials.email, loginCredentials.password);
      const user = userCredential.user;

      // Vérifier et enregistrer l'UID dans Supabase
      await axios.post('/api/auth/check-register-uid', {
        	uid: user.uid,
		    email: user.email
      });

      onLoginSuccess();
    } catch (error) {
      showAlert('error', "Erreur lors de la connexion.");
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (signupCredentials.password !== signupCredentials.confirmPassword) {
      showAlert('error', "Les mots de passe ne correspondent pas.");
      return;
    }
    try {
      await createUserWithEmailAndPassword(auth, signupCredentials.email, signupCredentials.password);
      showAlert('succes', "Inscription réussie !");
      setIsLogin(true);
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        showAlert('error', "Un compte existe déjà avec cette adresse e-mail.");
      } else if (error.message.includes('Password should be at least 6 characters')) {
        showAlert('error', "Le mot de passe doit contenir au moins 6 caractères.");
      } else {
        showAlert('error', "Inscription échouée.");
      }
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      showAlert('success', "Un email de réinitialisation de mot de passe a été envoyé.");
      setIsResetPassword(false);
    } catch (error) {
      showAlert('error', "Échec de la réinitialisation du mot de passe.");
    }
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      showAlert('success', 'Déconnexion réussie');
    } catch (error) {
      showAlert('error', "Échec de la déconnexion.")
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="auth-panel">
      {user ? (
        <div>
          <p>Vous êtes connecté en tant que {user.email}</p>
          <button onClick={handleLogout}>Déconnexion</button>
        </div>
      ) : (
        <>
          {isResetPassword ? (
            <div className="reset-password-form">
              <h2>Réinitialiser le mot de passe</h2>
              <form onSubmit={handleResetPasswordSubmit}>
                <div>
                  <label htmlFor="resetEmail">Email</label>
                  <input
                    type="email"
                    id="resetEmail"
                    name="resetEmail"
                    value={resetEmail}
                    onChange={handleResetEmailChange}
                  />
                </div>
                <button type="submit">Envoyer l'email de réinitialisation</button>
              </form>
              <p>
                <button onClick={() => setIsResetPassword(false)}>Retour à la connexion</button>
              </p>
            </div>
          ) : isLogin ? (
            <div className="login-form">
              <h2>Connexion</h2>
              <form onSubmit={handleLoginSubmit}>
                <div>
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={loginCredentials.email}
                    onChange={handleLoginChange}
                  />
                </div>
                <div className="password-input-container">
                  <label htmlFor="password">Mot de passe</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={loginCredentials.password}
                    onChange={handleLoginChange}
                  />
                  <span onClick={toggleShowPassword} className="password-toggle-icon">
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                <button type="submit">Se connecter</button>
              </form>
              <p>
                <button onClick={() => setIsResetPassword(true)}>Mot de passe oublié ?</button>
              </p>
              <p>
                Pas encore inscrit ? <button onClick={() => setIsLogin(false)}>Inscription</button>
              </p>
            </div>
          ) : (
            <div className="signup-form">
              <h2>Inscription</h2>
              <form onSubmit={handleSignupSubmit}>
                <div>
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={signupCredentials.email}
                    onChange={handleSignupChange}
                  />
                </div>
                <div className="password-input-container">
                  <label htmlFor="password">Mot de passe</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={signupCredentials.password}
                    onChange={handleSignupChange}
                  />
                  <span onClick={toggleShowPassword} className="password-toggle-icon">
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                <div className="password-input-container">
                  <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={signupCredentials.confirmPassword}
                    onChange={handleSignupChange}
                  />
                  <span onClick={toggleShowPassword} className="password-toggle-icon">
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                <button type="submit">S'inscrire</button>
              </form>
              <p>
                Déjà inscrit ? <button onClick={() => setIsLogin(true)}>Connexion</button>
              </p>
            </div>
          )}
            {alert && (
              <AlertMessage
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert(null)}
              />
            )}
        </>
      )}
    </div>
  );
};

export default AuthPanel;
